import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Card, EmptyState, Loading, ModuleHero, Notice, Spinner, StatCard, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import { DOC_STATUS, formatDateTime } from '../../lib/format';
import { CARD, CARD_STYLE } from '../../lib/ui';
import type { CompanyDocument, Requirement } from '../../types/workspace';

type DocsResponse = { requirements: Requirement[]; documents: CompanyDocument[]; missing: Requirement[] };

const STEPS = [
  { title: 'Read', body: 'Gemini reads the PDF or image — text, tables, seals and signatures.' },
  { title: 'Match', body: 'It checks the file is the document your country requires and matches your company name and registration number.' },
  { title: 'Validate', body: 'Dates, periods and completeness are checked — expired licences or missing pages are flagged.' },
  { title: 'Escalate', body: 'Anything uncertain goes to your Micus analyst for a human review.' },
];

export default function Verification() {
  const [polling, setPolling] = useState(false);
  const { data, loading, error, code } = useApi<DocsResponse>('/customer/documents', polling ? 4000 : undefined);
  const docs = data?.documents || [];
  const busy = docs.some((d) => d.status === 'VERIFYING' || d.status === 'UPLOADED');
  useEffect(() => setPolling(busy), [busy]);

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const count = (s: string) => docs.filter((d) => d.status === s).length;
  const issues = docs.filter((d) => d.status === 'NEEDS_REVIEW' || d.status === 'REJECTED');

  return (
    <div>
      <ModuleHero
        eyebrow="AI checks"
        title="AI verification"
        description="Every upload is verified by Micus AI against your country's document rules before your rating is generated."
        actions={
          busy ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1d4ed8]">
              <Spinner className="h-3 w-3" /> Verifying now
            </span>
          ) : undefined
        }
      />

      <div className="mz-fade-up mz-d1 mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Verified" value={count('VERIFIED')} tone="green" />
        <StatCard label="Needs review" value={count('NEEDS_REVIEW')} tone="amber" />
        <StatCard label="Rejected" value={count('REJECTED')} tone="rose" />
        <StatCard label="Missing required" value={data.missing.length} tone={data.missing.length ? 'pink' : 'slate'} to="/documents" />
      </div>

      {issues.length > 0 && (
        <div className="mt-5">
          <Notice tone="amber">
            {issues.length} document{issues.length > 1 ? 's need' : ' needs'} attention. Replace them from{' '}
            <Link to="/documents" className="font-bold underline">
              Documents
            </Link>{' '}
            — AI re-checks every new version automatically.
          </Notice>
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]">
        <div className="space-y-3">
          {docs.length === 0 && <EmptyState title="Nothing verified yet" body="Upload your first document and the AI check starts instantly." cta={{ label: 'Upload documents', to: '/documents' }} />}
          {docs.map((d) => {
            const s = DOC_STATUS[d.status] || { label: d.status, tone: 'slate' as const };
            return (
              <article key={d.id} className={`${CARD} p-4`} style={CARD_STYLE}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-[#6b0030]">{d.title}</h3>
                    <p className="text-[11px] text-[#c084a0]">
                      {d.fileName} · v{d.version} · checked {formatDateTime(d.aiCheckedAt)}
                    </p>
                  </div>
                  <StatusPill label={s.label} tone={s.tone} pulse={d.status === 'VERIFYING' || d.status === 'UPLOADED'} />
                </div>
                {d.aiConfidence != null && d.status !== 'VERIFYING' && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#fce7f3]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${d.aiConfidence}%`, background: d.aiConfidence >= 75 ? '#059669' : d.aiConfidence >= 45 ? '#d97706' : '#e11d48' }}
                      />
                    </div>
                    <span className="text-[11px] font-bold text-[#9d0050]">{d.aiConfidence}% confidence</span>
                  </div>
                )}
                {d.aiSummary && <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{d.aiSummary}</p>}
                {d.aiIssues && d.aiIssues.length > 0 && (
                  <ul className="mt-2 space-y-1 rounded-xl p-3" style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}>
                    {d.aiIssues.map((i, n) => (
                      <li key={n} className="text-xs text-[#be123c]">• {i}</li>
                    ))}
                  </ul>
                )}
                {d.reviewNote && (
                  <p className="mt-2 text-xs text-[#6b3a52]">
                    <span className="font-bold text-[#9d0050]">Analyst review{d.reviewedBy ? ` (${d.reviewedBy.name || d.reviewedBy.email})` : ''}: </span>
                    {d.reviewNote}
                  </p>
                )}
              </article>
            );
          })}
        </div>

        <Card title="How AI verification works" className="lg:sticky lg:top-24 lg:self-start">
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#fdf2f8] text-[11px] font-black text-[#d6006e]">{i + 1}</span>
                <div>
                  <p className="text-sm font-bold text-[#6b0030]">{s.title}</p>
                  <p className="text-xs leading-relaxed text-[#b0708f]">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}
